import React, { useState } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { passwordStrength } from '../../utils/formValidation';
import { inputBase, inputBorder } from './fieldStyles';
type PasswordFieldProps = {
  id: string;
  value: string;
  hasError: boolean;
  describedBy?: string;
  onChange: (value: string) => void;
  onBlur: () => void;
};
const strengthMeta = {
  1: {
    label: 'Weak',
    hint: 'Add numbers and more characters.',
    color: 'bg-[#D9483C]',
    text: 'text-[#C0362C]'
  },
  2: {
    label: 'Fair',
    hint: 'Add a capital letter and a symbol to make it stronger.',
    color: 'bg-[#D98A2B]',
    text: 'text-[#9A5B15]'
  },
  3: {
    label: 'Strong',
    hint: 'Great password.',
    color: 'bg-[#2E9A55]',
    text: 'text-[#1B7A3D]'
  }
} as const;
export function PasswordField({
  id,
  value,
  hasError,
  describedBy,
  onChange,
  onBlur
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  const strength = passwordStrength(value);
  return <div data-id="element-510" data-mp="3v3h16fapvmxh">
      <div className="relative" data-id="element-511" data-mp="3v3qplyg45m12">
        <input id={id} type={visible ? 'text' : 'password'} autoComplete="new-password" placeholder="At least 8 characters" value={value} required aria-required aria-invalid={hasError} aria-describedby={[describedBy, `${id}-strength`].filter(Boolean).join(' ')} onChange={e => onChange(e.target.value)} onBlur={onBlur} className={`${inputBase} ${inputBorder(hasError)} pr-12`} data-id="element-512" data-mp="8we5vk7srf23" />
        <button type="button" onClick={() => setVisible(v => !v)} aria-label={visible ? 'Hide password' : 'Show password'} aria-pressed={visible} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-[10px] text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-513" data-mp="wl75prrji71z">
          {visible ? <EyeOffIcon className="h-[18px] w-[18px]" data-id="element-514" data-mp="37drx81mnvrca" /> : <EyeIcon className="h-[18px] w-[18px]" data-id="element-515" data-mp="37ejh4f6wc0mq" />}
        </button>
      </div>
      <div id={`${id}-strength`} className="mt-2" aria-live="polite" data-id="element-516" data-mp="eihxlos366t8">
        {strength > 0 ? <>
            <div className="flex gap-1.5" aria-hidden="true" data-id="element-517" data-mp="9l87s80sbt4v">
              {[1, 2, 3].map(step => <span key={step} className={`h-1 flex-1 rounded-full ${step <= strength ? strengthMeta[strength].color : 'bg-canvas-line'}`} data-id="element-518" data-mp="nkr505s25k2e" />)}
            </div>
            <p className="mt-1.5 text-[13px] text-ink-muted" data-id="element-519" data-mp="xg2k4p7dotao">
              <span className={`font-semibold ${strengthMeta[strength].text}`} data-id="element-520" data-mp="si11249yctz7">{strengthMeta[strength].label}.</span>{' '}
              {strengthMeta[strength].hint}
            </p>
          </> : <p className="text-[13px] text-ink-muted" data-id="element-521" data-mp="12a6t1e07fbee">Use 8 or more characters with a mix of letters and numbers.</p>}
      </div>
    </div>;
}